import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import ComplaintWorkflowTimeline from '../components/ComplaintWorkflowTimeline';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Filter, Search, Eye, Sparkles, Loader2, Paperclip, Download, Tag, Check, Info, RotateCcw, User as UserIcon, Wrench, Package, Trash2, UserPlus, ClipboardList, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { generateReportPDF } from '../lib/pdf';
import { downloadEntityPdf } from '../lib/entityPdf';
import RowActions from '../components/RowActions';
import FileUpload from '../components/FileUpload';
import ComplaintMessages from '../components/ComplaintMessages';
import BuildingFilter from '../components/BuildingFilter';
import TagsInput from '../components/TagsInput';

const STAFF_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'TECHNICIAN', 'ACCOUNTANT'];

export default function Complaints() {
  const { user } = useContext(AuthContext);
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [complaints, setComplaints] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTenant, setFilterTenant] = useState('all');
  const [filterTechnician, setFilterTechnician] = useState('all');
  const [filterRepeated, setFilterRepeated] = useState('all'); // 'all' | 'yes' | 'no'
  const [filterMaterial, setFilterMaterial] = useState('');
  const [filterApartment, setFilterApartment] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBuildingId, setFilterBuildingId] = useState('all');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [forceClosing, setForceClosing] = useState(null);
  const [forceCloseReason, setForceCloseReason] = useState('');
  const [forceCloseMethod, setForceCloseMethod] = useState('phone');
  const [assigneeDraft, setAssigneeDraft] = useState('');
  const [repeatedNotesDraft, setRepeatedNotesDraft] = useState('');
  // Quick-assign popover: complaint being assigned inline from the row (skips the full dialog)
  const [quickAssignComplaint, setQuickAssignComplaint] = useState(null);
  const [quickAssignTech, setQuickAssignTech] = useState('');
  const [quickAssignBusy, setQuickAssignBusy] = useState(false);
  const [savingAssign, setSavingAssign] = useState(false);
  const [aiLoading, setAiLoading] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState({});
  // Phase 36 — Engineer/Manager can assign technicians. Everyone in the
  // management chain can trigger the assign UI.
  const ASSIGN_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER'];
  const canAssign = ASSIGN_ROLES.includes(user?.role);
  // Advanced search / filter bar is only shown to operational roles.
  // Tenants and Technicians get a simplified, focused list.
  const SEARCH_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER', 'STOREKEEPER', 'PROCUREMENT_OFFICER'];
  const canAdvancedSearch = SEARCH_ROLES.includes(user?.role);
  // Phase 35c — role-aware "assigned to me" filter. Defaults ON for TECHNICIAN
  // so they land on their active queue instead of the whole company's list.
  const [scopeMine, setScopeMine] = useState(
    searchParams.get('scope') === 'mine' || (user?.role === 'TECHNICIAN' && !searchParams.get('scope'))
  );

  const [newComplaint, setNewComplaint] = useState({
    building_id: '',
    tenant_id: '',
    title: '',
    description: '',
    category: 'maintenance',
    priority: 'medium',
    attachments: [],  // array of URLs
    tags: [],
    is_repeated: false,
  });
  const [company, setCompany] = useState(null);

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  // Phase 38 — Refetch when tab regains focus / becomes visible so admin sees
  // status changes made by engineer / technician in real time (no manual refresh).
  useEffect(() => {
    const onFocus = () => fetchData();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  const fetchData = async () => {
    try {
      const [complaintsRes, buildingsRes, companiesRes, usersRes, tenantsRes] = await Promise.all([
        axios.get(`${API}/complaints${filterStatus !== 'all' ? `?status=${filterStatus}` : ''}`, {
          withCredentials: true
        }),
        axios.get(`${API}/buildings`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }).catch(() => ({ data: [] })),
        // Phase 36 — engineers/managers need the technician roster to assign complaints
        canAssign
          ? axios.get(`${API}/users`, { withCredentials: true }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        // Phase 37 — tenant list for the create-complaint tenant picker + filter
        axios.get(`${API}/tenants`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setComplaints(complaintsRes.data);
      setBuildings(buildingsRes.data);
      setCompany(companiesRes.data.find(x => x.id === user?.company_id) || companiesRes.data[0]);
      setTechnicians((usersRes.data || []).filter(u => u.role === 'TECHNICIAN' && u.is_active !== false));
      setTenants(tenantsRes.data || []);
      // Feb-2026 — Complaint Command Center: if arriving with ?highlight=<id>
      // auto-open the dialog so users land straight on the single-source view.
      const hl = searchParams.get('highlight');
      if (hl) {
        const target = (complaintsRes.data || []).find(c => c.id === hl);
        if (target) setSelectedComplaint(target);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const assignTechnician = async () => {
    if (!selectedComplaint || !assigneeDraft) return;
    setSavingAssign(true);
    try {
      // Phase 38 — On assignment, status flips to 'assigned' (not 'in_progress').
      // Technician's own interaction later flips it to in_progress.
      const nextStatus = ['new', 'open', undefined, null, ''].includes(selectedComplaint.status)
        ? 'assigned' : selectedComplaint.status;
      await axios.put(`${API}/complaints/${selectedComplaint.id}`, {
        assigned_to: assigneeDraft, status: nextStatus,
      }, { withCredentials: true });
      const tech = technicians.find(t => t.id === assigneeDraft);
      toast.success(`Assigned to ${tech?.name || 'technician'} · technician notified · Work Order auto-created`);
      setSelectedComplaint({ ...selectedComplaint, assigned_to: assigneeDraft, status: nextStatus });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assign failed');
    } finally {
      setSavingAssign(false);
    }
  };

  // Row-level quick-assign (no need to open full complaint dialog)
  const quickAssignSave = async () => {
    if (!quickAssignComplaint || !quickAssignTech) return;
    setQuickAssignBusy(true);
    try {
      const nextStatus = ['new', 'open', undefined, null, ''].includes(quickAssignComplaint.status)
        ? 'assigned' : quickAssignComplaint.status;
      await axios.put(`${API}/complaints/${quickAssignComplaint.id}`, {
        assigned_to: quickAssignTech, status: nextStatus,
      }, { withCredentials: true });
      const tech = technicians.find(t => t.id === quickAssignTech);
      toast.success(`Assigned to ${tech?.name || 'technician'} · Work Order auto-created`);
      setQuickAssignComplaint(null); setQuickAssignTech('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assign failed');
    } finally { setQuickAssignBusy(false); }
  };

  const handleCreateComplaint = async () => {
    try {
      const r = await axios.post(`${API}/complaints`, newComplaint, { withCredentials: true });
      const ticket = r.data?.ticket_number;
      toast.success(ticket ? `Ticket ${ticket} created — save this reference!` : 'Complaint created successfully', { duration: 6000 });
      setShowNewDialog(false);
      setNewComplaint({
        building_id: '',
        tenant_id: '',
        title: '',
        description: '',
        category: 'maintenance',
        priority: 'medium',
        attachments: [],
        tags: [],
        is_repeated: false,
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create complaint');
    }
  };

  const saveRepeatedNotes = async () => {
    if (!selectedComplaint) return;
    try {
      await axios.put(`${API}/complaints/${selectedComplaint.id}`, {
        repeated_notes: repeatedNotesDraft,
      }, { withCredentials: true });
      toast.success('Justification saved');
      setSelectedComplaint({ ...selectedComplaint, repeated_notes: repeatedNotesDraft });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const confirmClosure = async (id) => {
    try {
      await axios.put(`${API}/complaints/${id}`, { tenant_confirmed: true, status: 'closed' }, { withCredentials: true });
      toast.success('Closure confirmed. Thank you!');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const downloadPDF = () => {
    generateReportPDF({
      title: 'Complaints Report',
      subtitle: `${complaints.length} records · ${filterStatus === 'all' ? 'All statuses' : filterStatus}`,
      company,
      sections: [{
        heading: 'Complaints',
        head: ['Ticket #', 'Date', 'Title', 'Raised by', 'Category', 'Priority', 'Status'],
        rows: filteredComplaints.slice(0, 100).map(c => [
          c.ticket_number || '—',
          new Date(c.created_at).toLocaleDateString(),
          c.title,
          c.reporter_name || '—',
          c.category,
          c.priority,
          c.status
        ])
      }]
    });
  };

  const getAISuggestion = async (complaintId) => {
    setAiLoading(complaintId);
    try {
      const r = await axios.post(`${API}/ai/complaint-suggestion/${complaintId}`, {}, { withCredentials: true });
      setAiSuggestions((prev) => ({ ...prev, [complaintId]: r.data.suggestion }));
      toast.success('AI suggestion ready');
    } catch { toast.error('AI suggestion failed'); }
    setAiLoading(null);
  };

  const handleUpdateStatus = async (complaintId, newStatus) => {
    try {
      await axios.put(
        `${API}/complaints/${complaintId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/complaints/${id}`, { withCredentials: true });
      toast.success('Complaint deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const downloadComplaintPdf = (c) => {
    const b = buildings.find(x => x.id === c.building_id);
    downloadEntityPdf({
      title: `Complaint · ${c.title}`,
      subtitle: `${c.category} · ${c.priority} priority · ${c.status}`,
      company,
      fields: [
        { label: 'Title', value: c.title },
        { label: 'Category', value: c.category },
        { label: 'Priority', value: c.priority },
        { label: 'Status', value: c.status },
        { label: 'Building', value: b?.name || c.building_id },
        { label: 'Created', value: new Date(c.created_at).toLocaleString() },
        { label: 'Resolved', value: c.resolved_at ? new Date(c.resolved_at).toLocaleString() : '—' },
        { label: 'Tenant confirmed', value: c.tenant_confirmed ? 'Yes' : 'No' },
        { label: 'AI suggestion', value: c.ai_suggestion ? 'Available' : '—' },
      ],
      notes: c.description + (c.ai_suggestion ? `\n\nAI Resolution Plan:\n${c.ai_suggestion}` : ''),
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-indigo-100 text-indigo-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const filteredComplaints = complaints.filter(complaint => {
    const st = searchTerm.toLowerCase();
    const matchSearch =
      (complaint.title || '').toLowerCase().includes(st) ||
      (complaint.description || '').toLowerCase().includes(st) ||
      (complaint.ticket_number || '').toLowerCase().includes(st) ||
      (complaint.tenant_name || '').toLowerCase().includes(st) ||
      (complaint.assigned_to_name || '').toLowerCase().includes(st);
    const matchBuilding = filterBuildingId === 'all' || complaint.building_id === filterBuildingId;
    const matchScope    = !scopeMine || complaint.assigned_to === user?.id || complaint.reported_by === user?.id;
    const matchTenant   = filterTenant === 'all' || complaint.tenant_id === filterTenant;
    const matchCategory = filterCategory === 'all' || complaint.category === filterCategory;
    const matchTech     =
      filterTechnician === 'all' ||
      (filterTechnician === 'unassigned' && !complaint.assigned_to) ||
      complaint.assigned_to === filterTechnician;
    const matchRepeated =
      filterRepeated === 'all' ||
      (filterRepeated === 'yes' && complaint.is_repeated) ||
      (filterRepeated === 'no'  && !complaint.is_repeated);
    const matchMaterial =
      !filterMaterial.trim() ||
      (complaint.materials_used || []).some(m => (m.item || '').toLowerCase().includes(filterMaterial.trim().toLowerCase()));
    const matchApartment =
      !filterApartment.trim() ||
      (complaint.tenant_unit || complaint.tenant_unit_number || complaint.unit_number || '').toLowerCase().includes(filterApartment.trim().toLowerCase()) ||
      (complaint.tenant_name || '').toLowerCase().includes(filterApartment.trim().toLowerCase());
    const matchDate = (() => {
      if (!filterDateFrom && !filterDateTo) return true;
      const d = complaint.created_at ? new Date(complaint.created_at).getTime() : 0;
      if (filterDateFrom && d < new Date(filterDateFrom).getTime()) return false;
      if (filterDateTo && d > new Date(filterDateTo).getTime() + 24 * 60 * 60 * 1000) return false;
      return true;
    })();
    return matchSearch && matchBuilding && matchScope && matchTenant && matchCategory && matchTech && matchRepeated && matchMaterial && matchApartment && matchDate;
  });

  const toggleScopeMine = () => {
    const next = !scopeMine;
    setScopeMine(next);
    const p = new URLSearchParams(searchParams);
    if (next) p.set('scope', 'mine'); else p.delete('scope');
    setSearchParams(p, { replace: true });
  };

  return (
    <Layout>
      <div data-testid="complaints-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t('comp.title')}</h1>
            <p className="text-muted-foreground">{t('comp.subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="download-complaints-pdf" onClick={downloadPDF} variant="outline"><Download className="w-4 h-4 mr-1" />PDF</Button>
            <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
            <DialogTrigger asChild>
              <Button data-testid="new-complaint-btn" className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                {t('comp.new')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Complaint</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Building</Label>
                  <Select
                    value={newComplaint.building_id}
                    onValueChange={(value) => setNewComplaint({ ...newComplaint, building_id: value, tenant_id: '' })}
                  >
                    <SelectTrigger data-testid="building-select">
                      <SelectValue placeholder="Select building" />
                    </SelectTrigger>
                    <SelectContent>
                      {buildings.map(building => (
                        <SelectItem key={building.id} value={building.id}>
                          {building.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {user?.role !== 'TENANT' && (
                  <div>
                    <Label>Tenant (optional)</Label>
                    <Select
                      value={newComplaint.tenant_id}
                      onValueChange={(value) => setNewComplaint({ ...newComplaint, tenant_id: value })}
                    >
                      <SelectTrigger data-testid="tenant-select">
                        <SelectValue placeholder={tenants.length === 0 ? 'No tenants set up' : (newComplaint.building_id ? 'Pick a tenant / unit…' : 'Pick a tenant (all buildings)')} />
                      </SelectTrigger>
                      <SelectContent>
                        {tenants
                          .filter(tn => !newComplaint.building_id || tn.building_id === newComplaint.building_id)
                          .map(tn => (
                            <SelectItem key={tn.id} value={tn.id} data-testid={`tenant-option-${tn.id}`}>
                              {tn.tenant_name || tn.contact_name} · {tn.unit_number}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground mt-1">Attach the tenant/unit so the technician has full context.</p>
                  </div>
                )}
                <div>
                  <Label>Title</Label>
                  <Input
                    data-testid="complaint-title-input"
                    value={newComplaint.title}
                    onChange={(e) => setNewComplaint({ ...newComplaint, title: e.target.value })}
                    placeholder="Brief description of the issue"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    data-testid="complaint-description-input"
                    value={newComplaint.description}
                    onChange={(e) => setNewComplaint({ ...newComplaint, description: e.target.value })}
                    placeholder="Detailed description"
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Select
                      value={newComplaint.category}
                      onValueChange={(value) => setNewComplaint({ ...newComplaint, category: value })}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="cleaning">Cleaning</SelectItem>
                        <SelectItem value="electrical">Electrical</SelectItem>
                        <SelectItem value="plumbing">Plumbing</SelectItem>
                        <SelectItem value="hvac">HVAC</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select
                      value={newComplaint.priority}
                      onValueChange={(value) => setNewComplaint({ ...newComplaint, priority: value })}
                    >
                      <SelectTrigger data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {user?.role !== 'TENANT' && (
                  <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/50 flex items-start gap-3">
                    <input
                      data-testid="is-repeated-checkbox"
                      id="is-repeated"
                      type="checkbox"
                      checked={!!newComplaint.is_repeated}
                      onChange={(e) => setNewComplaint({ ...newComplaint, is_repeated: e.target.checked })}
                      className="mt-1 w-4 h-4 accent-amber-600"
                    />
                    <label htmlFor="is-repeated" className="text-sm cursor-pointer">
                      <span className="font-semibold text-amber-900 flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" />Repeated complaint</span>
                      <span className="block text-xs text-amber-800 mt-0.5">
                        Tick this if the tenant has raised the same issue before. The technician / engineer will be prompted to add a justification note (why it&apos;s recurring — root cause, wear-and-tear, incomplete previous fix, tenant misuse, etc.).
                      </span>
                    </label>
                  </div>
                )}
                <div>
                  <Label>Attachments (photos / PDFs / videos)</Label>
                  <FileUpload value={newComplaint.attachments} onChange={(urls) => setNewComplaint({ ...newComplaint, attachments: urls })} maxFiles={5} />
                </div>
                <div>
                  <Label>Tags</Label>
                  <TagsInput value={newComplaint.tags} onChange={(tags) => setNewComplaint({ ...newComplaint, tags })} testIdPrefix="complaint-tag" />
                </div>
                <Button
                  data-testid="submit-complaint-btn"
                  onClick={handleCreateComplaint}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={!newComplaint.building_id || !newComplaint.title}
                >
                  Create Complaint
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Filters — advanced search is hidden for Tenants & Technicians */}
        {canAdvancedSearch && (
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                data-testid="search-input"
                placeholder="Search complaints..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="status-filter" className="w-40">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <BuildingFilter value={filterBuildingId} onChange={setFilterBuildingId} buildings={buildings} />
            <Button
              type="button"
              variant={showFilters ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowFilters(v => !v)}
              className={showFilters ? 'bg-slate-800 hover:bg-slate-900 text-white' : ''}
              title="Show / hide advanced filters (tenant, technician, category, repeated, material)"
              data-testid="toggle-advanced-filters"
            >
              <Filter className="w-3.5 h-3.5 mr-1" />More Filters
            </Button>
            {user?.role !== 'TENANT' && (
              <Button
                data-testid="scope-mine-toggle"
                type="button"
                variant={scopeMine ? 'default' : 'outline'}
                size="sm"
                onClick={toggleScopeMine}
                className={scopeMine ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}
                title="Show only complaints assigned to me or raised by me"
              >
                <Check className={`w-3.5 h-3.5 mr-1 ${scopeMine ? 'opacity-100' : 'opacity-30'}`} />
                Assigned to me
              </Button>
            )}
          </div>
          {showFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Tenant</Label>
                <Select value={filterTenant} onValueChange={setFilterTenant}>
                  <SelectTrigger data-testid="filter-tenant" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All tenants</SelectItem>
                    {tenants.map(tn => (
                      <SelectItem key={tn.id} value={tn.id}>{tn.tenant_name || tn.contact_name} · {tn.unit_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Category</Label>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger data-testid="filter-category" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="cleaning">Cleaning</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="hvac">HVAC</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Attended by</Label>
                <Select value={filterTechnician} onValueChange={setFilterTechnician}>
                  <SelectTrigger data-testid="filter-technician" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All technicians</SelectItem>
                    <SelectItem value="unassigned">— Unassigned —</SelectItem>
                    {technicians.map(tec => (
                      <SelectItem key={tec.id} value={tec.id}>{tec.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Repeated</Label>
                <Select value={filterRepeated} onValueChange={setFilterRepeated}>
                  <SelectTrigger data-testid="filter-repeated" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All (repeated + first-time)</SelectItem>
                    <SelectItem value="yes">🔁 Repeated only</SelectItem>
                    <SelectItem value="no">First-time only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Material used contains</Label>
                <Input
                  data-testid="filter-material"
                  value={filterMaterial}
                  onChange={(e) => setFilterMaterial(e.target.value)}
                  placeholder="e.g. compressor, drill"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Apartment / Unit</Label>
                <Input
                  data-testid="filter-apartment"
                  value={filterApartment}
                  onChange={(e) => setFilterApartment(e.target.value)}
                  placeholder="e.g. 305, Villa 12"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">From date</Label>
                <Input
                  data-testid="filter-date-from"
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">To date</Label>
                <Input
                  data-testid="filter-date-to"
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          )}
        </Card>
        )}

        {/* Complaints List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredComplaints.map((complaint) => (
              <Card
                key={complaint.id}
                data-testid={`complaint-card-${complaint.id}`}
                className="p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex-1">
                        {complaint.ticket_number && (
                          <button
                            type="button"
                            data-testid={`complaint-ticket-${complaint.id}`}
                            title="Click to copy ticket number"
                            onClick={() => { try { navigator.clipboard.writeText(complaint.ticket_number); toast.success('Ticket # copied'); } catch (e) { /* clipboard denied */ } }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 border border-amber-500/40 text-[11px] font-mono font-semibold tracking-wide hover:bg-amber-500/20 mb-2"
                          >
                            <Tag className="w-3 h-3" />{complaint.ticket_number}
                          </button>
                        )}
                        <h3 className="text-lg font-semibold text-foreground mb-1">
                          {complaint.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-2">{complaint.description}</p>
                        {(complaint.reporter_name || complaint.reporter_phone || complaint.reporter_email) && (
                          <div className="text-xs text-muted-foreground mb-2 flex flex-wrap gap-x-3 gap-y-1">
                            {complaint.reporter_name && <span><strong className="text-muted-foreground">Raised by:</strong> {complaint.reporter_name}</span>}
                            {complaint.reporter_phone && <span>☎ {complaint.reporter_phone}</span>}
                            {complaint.reporter_email && <span>✉ {complaint.reporter_email}</span>}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <span
                            title="Tenant-facing complaint status. The linked Work Order and Service Report have their own separate document-lifecycle statuses (visible on those pages)."
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(complaint.status)}`}
                          >
                            {complaint.status === 'tenant_sign_pending'
                              ? 'tenant confirmation pending'
                              : (complaint.status || 'open').replace('_', ' ')}
                          </span>
                          {complaint.latest_report_status && (
                            <span
                              data-testid={`sr-status-${complaint.id}`}
                              title="Service Report document status — where the report is in its own lifecycle."
                              className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-800/90 text-white inline-flex items-center gap-1"
                            >
                              · {({
                                materials_pending:  'materials pending',
                                materials_approved: 'materials approved',
                                materials_issued:   'materials issued',
                                completed:          'awaiting engineer verify',
                                signed:             'tenant signed',
                                approved:           'verified',
                                rejected:           'sr rejected',
                              })[complaint.latest_report_status] || complaint.latest_report_status}
                            </span>
                          )}
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(complaint.priority)}`}>
                            {complaint.priority}
                          </span>
                          <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold capitalize">
                            {complaint.category}
                          </span>
                          {complaint.is_repeated && (
                            <span
                              data-testid={`repeated-badge-${complaint.id}`}
                              title="Marked as a repeated / recurring complaint. Justification note added by technician or engineer."
                              className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold inline-flex items-center gap-1 border border-amber-300"
                            >
                              <RotateCcw className="w-3 h-3" />Repeated
                            </span>
                          )}
                          {complaint.next_action && complaint.status !== 'closed' && (
                            <span
                              data-testid={`next-action-${complaint.id}`}
                              title={`Currently with: ${complaint.current_owner_role || 'Closed'}`}
                              className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/10 border border-amber-400 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200 inline-flex items-center gap-1"
                            >
                              👉 {complaint.current_owner_role || 'Done'} · {complaint.next_action.length > 45 ? complaint.next_action.slice(0, 43) + '…' : complaint.next_action}
                            </span>
                          )}
                        </div>
                        {/* Phase 37 — context strip: tenant / technician / materials / closure */}
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-4 text-[12px] text-slate-600">
                          {complaint.tenant_name && (
                            <div data-testid={`complaint-tenant-${complaint.id}`} className="flex items-center gap-1.5" title="Tenant / unit affected">
                              <UserIcon className="w-3.5 h-3.5 text-slate-400" />
                              <span className="text-slate-500">Tenant:</span>
                              <span className="font-medium text-slate-800">{complaint.tenant_name}{complaint.tenant_unit ? ` · ${complaint.tenant_unit}` : ''}</span>
                            </div>
                          )}
                          {complaint.assigned_to_name && (
                            <div data-testid={`complaint-tech-${complaint.id}`} className="flex items-center gap-1.5" title="Technician the engineer assigned">
                              <Wrench className="w-3.5 h-3.5 text-slate-400" />
                              <span className="text-slate-500">Attended by:</span>
                              <span className="font-medium text-slate-800">{complaint.assigned_to_name}</span>
                            </div>
                          )}
                          {(complaint.materials_used || []).length > 0 && (
                            <div data-testid={`complaint-materials-${complaint.id}`} className="flex items-start gap-1.5 sm:col-span-2" title="Materials issued for the fix (as recorded on the service report)">
                              <Package className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
                              <span className="text-slate-500">Materials used:</span>
                              <span className="font-medium text-slate-800">
                                {complaint.materials_used.slice(0, 4).map(m => `${m.item} × ${m.qty}${m.unit || ''}`).join(' · ')}
                                {complaint.materials_used.length > 4 && ` +${complaint.materials_used.length - 4} more`}
                              </span>
                            </div>
                          )}
                          {complaint.last_service_report_by_name && (
                            <div data-testid={`complaint-work-done-${complaint.id}`} className="flex items-center gap-1.5" title="Technician who submitted the latest service report">
                              <Check className="w-3.5 h-3.5 text-emerald-500" />
                              <span className="text-slate-500">Work completed by:</span>
                              <span className="font-medium text-slate-800">
                                {complaint.last_service_report_by_name}
                                {complaint.last_service_report_at && (
                                  <span className="text-slate-400 ml-1">· {new Date(complaint.last_service_report_at).toLocaleDateString()}</span>
                                )}
                              </span>
                            </div>
                          )}
                          {complaint.closed_by_name && (
                            <div data-testid={`complaint-closed-by-${complaint.id}`} className="flex items-center gap-1.5" title="Engineer / manager who signed off closure">
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                              <span className="text-slate-500">Closed by:</span>
                              <span className="font-medium text-emerald-800">{complaint.closed_by_name}</span>
                              {complaint.closed_at && (
                                <span className="text-slate-400 text-[11px]">· {new Date(complaint.closed_at).toLocaleDateString()}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Created: {complaint.created_at ? new Date(complaint.created_at).toLocaleString() : '—'}
                    </div>
                    {(complaint.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {complaint.tags.map((tg, i) => <span key={i} data-testid={`complaint-${complaint.id}-tag-${i}`} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">{tg}</span>)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {STAFF_ROLES.includes(user?.role) && complaint.status !== 'closed' && (
                      <Button
                        data-testid={`ai-suggest-${complaint.id}`}
                        onClick={() => getAISuggestion(complaint.id)}
                        disabled={aiLoading === complaint.id}
                        variant="outline"
                        size="sm"
                        title="Ask AI for a resolution suggestion for this complaint"
                        className="text-purple-600 border-purple-300 hover:bg-purple-50"
                      >
                        {aiLoading === complaint.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" />AI Suggest</>}
                      </Button>
                    )}
                    {user?.role !== 'TENANT' && complaint.status !== 'closed' && (
                      <div className="flex items-center gap-1">
                        <Select
                          value={complaint.status}
                          onValueChange={(value) => handleUpdateStatus(complaint.id, value)}
                        >
                          <SelectTrigger data-testid={`status-update-${complaint.id}`} className="w-44" title="Change complaint status — see the ⓘ icon for who can do what">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open — Just raised, not yet assigned</SelectItem>
                            <SelectItem value="assigned">Assigned — Technician picked, not started</SelectItem>
                            <SelectItem value="in_progress">In Progress — Technician actively working</SelectItem>
                            <SelectItem value="waiting_for_material">Waiting for Material — Parts requested</SelectItem>
                            <SelectItem value="tenant_sign_pending">Tenant Confirmation Pending — Awaiting tenant confirmation</SelectItem>
                            <SelectItem value="resolved">Resolved — Technician says done</SelectItem>
                            <SelectItem value="closed">Closed — Engineer signed off</SelectItem>
                          </SelectContent>
                        </Select>
                        <button
                          type="button"
                          className="text-slate-400 hover:text-slate-700"
                          title={
                            'Who can change what:\n' +
                            '• Tenant — cannot change status; only "Confirm closure" when resolved\n' +
                            '• Technician — up to "resolved" (via service report submission)\n' +
                            '• Engineer / CS Supervisor / Manager / Admin — full control incl. "closed"\n\n' +
                            'Automatic transitions (new Phase-38 flow):\n' +
                            '• Engineer assigns technician → assigned (was in_progress before)\n' +
                            '• Technician engages with the ticket → in_progress\n' +
                            '• Technician requests materials on a report → waiting_for_material\n' +
                            '• Storekeeper issues materials → back to in_progress\n' +
                            '• Technician submits final report → tenant confirmation pending\n' +
                            '• Tenant signs + Engineer approves report → closed'
                          }
                          data-testid={`status-info-${complaint.id}`}
                          aria-label="Status permission info"
                        >
                          <Info className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <Button
                      data-testid={`view-complaint-${complaint.id}`}
                      variant="outline"
                      size="sm"
                      title="View complaint details, assign a technician, and read tenant messages"
                      onClick={() => {
                        setAssigneeDraft(complaint.assigned_to || '');
                        setRepeatedNotesDraft(complaint.repeated_notes || '');
                        setSelectedComplaint(complaint);
                        // Phase 38 — Auto-flip 'assigned' → 'in_progress' when the
                        // assigned technician opens the dialog for the first time.
                        // The backend enforces the role/ownership check.
                        if (
                          complaint.assigned_to === user?.id
                          && complaint.status === 'assigned'
                          && user?.role === 'TECHNICIAN'
                        ) {
                          axios.put(`${API}/complaints/${complaint.id}`, {}, { withCredentials: true })
                            .then(() => fetchData())
                            .catch(() => {});
                        }
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>

                    {/* Row-level Quick Assign — engineers/managers assign a technician
                        in one click without opening the full dialog. Also visible when
                        already assigned (label: "Reassign") to allow fast reroutes. */}
                    {canAssign && complaint.status !== 'closed' && (
                      <Button
                        data-testid={`quick-assign-btn-${complaint.id}`}
                        variant="outline"
                        size="sm"
                        title="Assign or reassign to a technician (auto-creates Work Order)"
                        className="border-teal-300 text-teal-700 hover:bg-teal-50"
                        onClick={() => {
                          setQuickAssignComplaint(complaint);
                          setQuickAssignTech(complaint.assigned_to || '');
                        }}
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        {complaint.assigned_to ? 'Reassign' : 'Assign'}
                      </Button>
                    )}

                    {/* View linked Work Order — jump straight to the WO created for this complaint */}
                    {complaint.linked_wo_id && (
                      <Button
                        data-testid={`view-wo-btn-${complaint.id}`}
                        variant="outline"
                        size="sm"
                        title={`Jump to ${complaint.linked_wo_number || 'linked Work Order'}`}
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                        onClick={() => { window.location.href = `/work-orders?highlight=${complaint.linked_wo_id}`; }}
                      >
                        <ClipboardList className="w-4 h-4 mr-1" />WO
                      </Button>
                    )}
                    <RowActions
                      testIdPrefix={`complaint-${complaint.id}`}
                      onView={() => { setAssigneeDraft(complaint.assigned_to || ''); setRepeatedNotesDraft(complaint.repeated_notes || ''); setSelectedComplaint(complaint); }}
                      onDownloadPdf={() => downloadComplaintPdf(complaint)}
                      onDelete={['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'].includes(user?.role) ? () => handleDelete(complaint.id) : undefined}
                      deleteLabel={`complaint "${complaint.title}"`}
                    />
                  </div>
                </div>
                {(aiSuggestions[complaint.id] || complaint.ai_suggestion) && (
                  <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-purple-700 mb-1">
                      <Sparkles className="w-3 h-3" />AI Resolution Suggestion
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{aiSuggestions[complaint.id] || complaint.ai_suggestion}</p>
                  </div>
                )}
                {complaint.attachments?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {complaint.attachments.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-teal-600 hover:underline bg-teal-50 px-2 py-1 rounded">
                        <Paperclip className="w-3 h-3" />Attachment {i + 1}
                      </a>
                    ))}
                  </div>
                )}
                {user?.role === 'TENANT' && complaint.status === 'resolved' && !complaint.tenant_confirmed && complaint.reported_by === user.id && (
                  <div className="mt-3 flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">Issue reported as resolved. Please confirm to close.</p>
                    <Button data-testid={`tenant-confirm-${complaint.id}`} onClick={() => confirmClosure(complaint.id)} size="sm" className="bg-green-600 hover:bg-green-700"><Check className="w-4 h-4 mr-1" />Confirm Closure</Button>
                  </div>
                )}
                {complaint.tenant_confirmed && (
                  <div className="mt-3 flex items-center gap-1 text-xs text-green-700 font-semibold">
                    <Check className="w-3 h-3" />Tenant confirmed closure
                  </div>
                )}
              </Card>
            ))}
            {filteredComplaints.length === 0 && (
              <Card className="p-12 text-center">
                <p className="text-muted-foreground">No complaints found</p>
              </Card>
            )}
          </div>
        )}

        {/* View complaint dialog with messages */}
        {selectedComplaint && (
          <Dialog open={!!selectedComplaint} onOpenChange={(o) => { if (!o) { setSelectedComplaint(null); setAssigneeDraft(''); setRepeatedNotesDraft(''); } }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{selectedComplaint.title}</DialogTitle></DialogHeader>
              {selectedComplaint.next_action && selectedComplaint.status !== 'closed' && (
                <div
                  data-testid="complaint-owner-banner"
                  className="rounded-lg border-2 border-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 flex items-center gap-3"
                >
                  <div className="text-2xl">👉</div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide font-semibold text-amber-800 dark:text-amber-300">Currently with</div>
                    <div className="text-base font-bold text-amber-900 dark:text-amber-200">{selectedComplaint.current_owner_role || '—'}</div>
                    <div className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">Next action: {selectedComplaint.next_action}</div>
                  </div>
                </div>
              )}
              <ComplaintWorkflowTimeline complaint={selectedComplaint} />
              <div className="space-y-2 mt-2">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 rounded-full bg-slate-100 capitalize">{selectedComplaint.category}</span>
                  <span className="px-2 py-1 rounded-full bg-slate-100 capitalize">{selectedComplaint.priority}</span>
                  <span className="px-2 py-1 rounded-full bg-slate-100 capitalize">{selectedComplaint.status}</span>
                  {selectedComplaint.assigned_to && (
                    <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">
                      Assigned: {technicians.find(t => t.id === selectedComplaint.assigned_to)?.name || selectedComplaint.assigned_to.slice(0, 8)}
                    </span>
                  )}
                  {/* View linked Work Order — auto-created on assignment. Jumps to
                      Work Orders page with the WO row highlighted. */}
                  {selectedComplaint.linked_wo_id && (
                    <button
                      data-testid="dialog-view-wo-pill"
                      onClick={() => { window.location.href = `/work-orders?highlight=${selectedComplaint.linked_wo_id}`; }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-100 text-purple-800 hover:bg-purple-200 font-medium transition"
                      title="Open the Work Order created for this complaint"
                    >
                      <ClipboardList className="w-3 h-3" />
                      {selectedComplaint.linked_wo_number || 'View Work Order'}
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedComplaint.description}</p>

                {/* Phase 37 — Repeated complaint justification (edited by Technician/Engineer) */}
                {selectedComplaint.is_repeated && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-300">
                    <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 mb-2">
                      <RotateCcw className="w-4 h-4" />
                      This is a REPEATED complaint
                    </div>
                    {(['TECHNICIAN', 'ENGINEER', 'MANAGER', 'CS_SUPERVISOR', 'SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'].includes(user?.role)) ? (
                      <>
                        <Label className="text-[11px] uppercase tracking-wide text-amber-800">
                          Justification / root-cause note
                          <span className="text-amber-600 ml-1">(add why this issue is recurring)</span>
                        </Label>
                        <Textarea
                          data-testid="repeated-notes-input"
                          value={repeatedNotesDraft}
                          onChange={(e) => setRepeatedNotesDraft(e.target.value)}
                          placeholder="e.g. Same compressor fault — previous fix used a refurbished part. Recommending full unit replacement."
                          rows={3}
                          className="mt-1 bg-white"
                        />
                        <div className="mt-2 flex justify-end">
                          <Button
                            data-testid="save-repeated-notes-btn"
                            size="sm"
                            onClick={saveRepeatedNotes}
                            disabled={repeatedNotesDraft === (selectedComplaint.repeated_notes || '')}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                          >
                            <Check className="w-3.5 h-3.5 mr-1" />Save justification
                          </Button>
                        </div>
                      </>
                    ) : (
                      selectedComplaint.repeated_notes ? (
                        <p className="text-sm text-amber-900 whitespace-pre-wrap">{selectedComplaint.repeated_notes}</p>
                      ) : (
                        <p className="text-xs text-amber-700 italic">No justification added yet — technician / engineer will note the root cause.</p>
                      )
                    )}
                  </div>
                )}
                {selectedComplaint.attachments?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedComplaint.attachments.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-teal-600 hover:underline bg-teal-50 px-2 py-1 rounded">
                        Attachment {i + 1}
                      </a>
                    ))}
                  </div>
                )}

                {/* Phase 36 — Engineer / Manager assigns a technician */}
                {canAssign && selectedComplaint.status !== 'closed' && (
                  <div className="pt-3 border-t">
                    <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Assign to Technician</Label>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Select
                        value={assigneeDraft || selectedComplaint.assigned_to || ''}
                        onValueChange={setAssigneeDraft}
                      >
                        <SelectTrigger data-testid="assign-technician-select" className="w-64">
                          <SelectValue placeholder={technicians.length ? 'Pick a technician…' : 'No technicians available'} />
                        </SelectTrigger>
                        <SelectContent>
                          {technicians.map(tec => (
                            <SelectItem key={tec.id} value={tec.id} data-testid={`tech-option-${tec.id}`}>
                              {tec.name} · {tec.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        data-testid="save-assignment-btn"
                        onClick={assignTechnician}
                        disabled={!assigneeDraft || savingAssign || assigneeDraft === selectedComplaint.assigned_to}
                        size="sm"
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        {savingAssign ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                        {selectedComplaint.assigned_to ? 'Reassign' : 'Assign'}
                      </Button>
                      {selectedComplaint.assigned_to && (
                        <span className="text-xs text-slate-500">
                          Currently: {technicians.find(t => t.id === selectedComplaint.assigned_to)?.name || '—'}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <ComplaintMessages complaintId={selectedComplaint.id} />

                {/* Phase 38 — Quick actions from the complaint dialog so the
                    technician doesn't have to hunt through /reports for the
                    material-request form or the sign-off. */}
                {selectedComplaint.status !== 'closed' && (
                  <div className="pt-3 border-t space-y-2">
                    <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Quick actions</Label>
                    <div className="flex flex-wrap gap-2">
                      {user?.id === selectedComplaint.assigned_to && user?.role === 'TECHNICIAN' && (
                        <Button
                          data-testid="quick-request-material-btn"
                          size="sm"
                          onClick={() => {
                            // Go to Service Reports with a query param that tells that page
                            // to pre-open the "New Report" form linked to this complaint.
                            window.location.href = `/reports?new=1&complaint_id=${selectedComplaint.id}`;
                          }}
                          className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                          title="Open a new service report pre-linked to this complaint and add material requests"
                        >
                          <Package className="w-4 h-4 mr-1" />Request Materials / Submit Report
                        </Button>
                      )}
                      {['ENGINEER', 'CS_SUPERVISOR', 'MANAGER', 'FULL_ACCESS', 'ADMIN', 'CEO', 'SUPERADMIN'].includes(user?.role) && (
                        <Button
                          data-testid="quick-goto-approvals"
                          size="sm"
                          variant="outline"
                          onClick={() => (window.location.href = '/approvals')}
                          title="Go to the Approvals hub for material decisions and report sign-offs"
                        >
                          <Check className="w-4 h-4 mr-1" />Approvals hub
                        </Button>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Workflow: request materials → engineer approves → storekeeper issues → work done → tenant signs → engineer closes.
                    </p>
                  </div>
                )}
                {selectedComplaint.status !== 'resolved' && selectedComplaint.status !== 'closed' && ASSIGN_ROLES.includes(user?.role) && (
                  <div className="pt-3 border-t flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs text-muted-foreground">Dispatch a Work Order to execute this complaint.</div>
                    <Button
                      data-testid="dispatch-wo-btn"
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                      onClick={async () => {
                        try {
                          const r = await axios.post(`${API}/work-orders/from-complaint/${selectedComplaint.id}`, { executor_type: 'internal' }, { withCredentials: true });
                          toast.success(`Work order ${r.data.wo_number} dispatched`);
                          setSelectedComplaint(null);
                          window.location.href = `/work-orders`;
                        } catch (e) { toast.error(e.response?.data?.detail || 'Dispatch failed'); }
                      }}
                    >Dispatch Work Order</Button>
                  </div>
                )}
                {/* Feb-2026 — Force-Close: for tenants unreachable / not signing.
                    Engineer/Manager confirm verbally via Phone/WhatsApp/Email and
                    record a mandatory reason note that goes into the audit log. */}
                {selectedComplaint.status !== 'closed' && ASSIGN_ROLES.includes(user?.role) && (
                  <div className="pt-3 border-t flex flex-wrap items-center justify-between gap-2 bg-rose-50/40 dark:bg-rose-950/20 rounded p-2">
                    <div className="text-xs text-slate-700 dark:text-slate-300">
                      <div className="font-semibold text-rose-700 dark:text-rose-300">Force-Close Complaint</div>
                      <div>Use when tenant confirmed verbally / via WhatsApp / email but won&apos;t sign in-app. Reason is recorded on the audit log.</div>
                    </div>
                    <Button
                      data-testid="force-close-btn"
                      size="sm"
                      variant="outline"
                      className="border-rose-400 text-rose-700 hover:bg-rose-100 dark:text-rose-300"
                      onClick={() => setForceClosing(selectedComplaint)}
                    >Force Close</Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Quick-assign modal opened from the complaint row (no full dialog).
            Engineers/Managers pick a technician in one click; backend auto-creates
            the Work Order + audit comment. */}
        {quickAssignComplaint && (
          <Dialog open={!!quickAssignComplaint} onOpenChange={(o) => { if (!o) { setQuickAssignComplaint(null); setQuickAssignTech(''); } }}>
            <DialogContent data-testid="quick-assign-dialog" className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-teal-600" />
                  {quickAssignComplaint.assigned_to ? 'Reassign' : 'Assign'} technician
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div className="text-sm p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="font-medium text-slate-800 truncate">{quickAssignComplaint.title}</div>
                  <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-1.5">
                    <span className="px-1.5 py-0.5 rounded bg-white border">{quickAssignComplaint.ticket_number}</span>
                    <span className="px-1.5 py-0.5 rounded bg-white border capitalize">{quickAssignComplaint.priority}</span>
                    <span className="px-1.5 py-0.5 rounded bg-white border capitalize">{quickAssignComplaint.category}</span>
                    <span className="px-1.5 py-0.5 rounded bg-white border capitalize">{quickAssignComplaint.status}</span>
                  </div>
                </div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Technician</Label>
                <Select value={quickAssignTech} onValueChange={setQuickAssignTech}>
                  <SelectTrigger data-testid="quick-assign-tech-select">
                    <SelectValue placeholder={technicians.length ? 'Pick a technician…' : 'No technicians available'} />
                  </SelectTrigger>
                  <SelectContent>
                    {technicians.map(tec => (
                      <SelectItem key={tec.id} value={tec.id} data-testid={`quick-assign-option-${tec.id}`}>
                        {tec.name} · {tec.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-xs text-slate-500 p-2 rounded bg-purple-50 border border-purple-100">
                  <ClipboardList className="w-3 h-3 inline mr-1 text-purple-600" />
                  A Work Order will be auto-created and linked to this complaint. Technician can request material from that WO.
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="ghost" onClick={() => { setQuickAssignComplaint(null); setQuickAssignTech(''); }}>Cancel</Button>
                  <Button
                    data-testid="quick-assign-save-btn"
                    onClick={quickAssignSave}
                    disabled={!quickAssignTech || quickAssignBusy || quickAssignTech === quickAssignComplaint.assigned_to}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    {quickAssignBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                    {quickAssignComplaint.assigned_to ? 'Reassign' : 'Assign'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Feb-2026 — Force-Close modal — Engineer/Manager records a mandatory
            reason + contact method; note goes into complaint thread + audit log. */}
        {forceClosing && (
          <Dialog open onOpenChange={(v) => { if (!v) { setForceClosing(null); setForceCloseReason(''); setForceCloseMethod('phone'); } }}>
            <DialogContent data-testid="force-close-dialog" className="max-w-md">
              <DialogHeader><DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5" />Force Close Complaint</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  Ticket <span className="font-mono">{forceClosing.ticket_number || forceClosing.id.slice(0, 8)}</span> — this will bypass the tenant-signature step.
                </div>
                <div>
                  <Label className="text-xs">How did you confirm with the tenant?</Label>
                  <Select value={forceCloseMethod} onValueChange={setForceCloseMethod}>
                    <SelectTrigger data-testid="force-close-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">📞 Phone call</SelectItem>
                      <SelectItem value="whatsapp">💬 WhatsApp</SelectItem>
                      <SelectItem value="email">✉️ Email</SelectItem>
                      <SelectItem value="in_person">🤝 In person</SelectItem>
                      <SelectItem value="sms">📱 SMS</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Reason / conversation summary <span className="text-rose-600">*</span></Label>
                  <Textarea
                    data-testid="force-close-reason"
                    rows={4}
                    placeholder="e.g. Called tenant Ms. Fatima at 14:20 — she confirmed AC is working, agrees to close ticket."
                    value={forceCloseReason}
                    onChange={(e) => setForceCloseReason(e.target.value)}
                  />
                  <div className="text-[10px] text-slate-500 mt-1">Recorded permanently on the complaint thread + audit log.</div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => { setForceClosing(null); setForceCloseReason(''); }}>Cancel</Button>
                  <Button
                    data-testid="force-close-submit"
                    disabled={!forceCloseReason.trim()}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/complaints/${forceClosing.id}/force-close`, { reason: forceCloseReason.trim(), contact_method: forceCloseMethod }, { withCredentials: true });
                        toast.success('Complaint force-closed — reason recorded on audit log');
                        setForceClosing(null); setForceCloseReason(''); setForceCloseMethod('phone');
                        setSelectedComplaint(null);
                        fetchAll();
                      } catch (e) { toast.error(e.response?.data?.detail || 'Force-close failed'); }
                    }}
                  >
                    <AlertTriangle className="w-4 h-4 mr-1" />Force Close
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}